import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isNewConditionsOn } from '../utils';
import { trackSideNavResetAlertShown } from '../utils/tracking/features/sideNavTracking';

const CONDITIONS_CHAPTER_INDEX = 1;

/**
 * Renders and tracks the side nav reset alert on the Conditions orientation page.
 * The alert is shown only for users in the side nav + new conditions experience
 * who have progressed past the Conditions chapter.
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data from Redux store
 * @returns {React.ReactElement|null} Alert component or null when not eligible
 */
function ConditionsSideNavResetAlert({ formData } = {}) {
  // Track whether we've already emitted the shown event for this mount
  const hasTrackedRef = useRef(false);
  const currentPathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const maxChapterIndex = formData?.['view:sideNavChapterIndex'] || 0;
  const shouldRenderAlert =
    formData?.disability526SidenavEnabled === true &&
    isNewConditionsOn(formData) &&
    maxChapterIndex > CONDITIONS_CHAPTER_INDEX;

  const conditionsExperienceUsed = isNewConditionsOn(formData) ? 'v2' : 'v1';

  // Track alert shown on first eligible render
  useEffect(() => {
    if (shouldRenderAlert && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackSideNavResetAlertShown({
        pathname: currentPathname,
        maxChapterIndex,
        conditionsExperienceUsed,
      });
    }
  }, [
    currentPathname,
    shouldRenderAlert,
    maxChapterIndex,
    conditionsExperienceUsed,
  ]);

  if (!shouldRenderAlert) {
    return null;
  }

  return (
    <va-alert status="info" class="vads-u-margin-top--0">
      <h3 slot="headline">You’re back to Step 2: Conditions</h3>
      <p>We saved your previous responses. You won’t lose any information.</p>
      <p>
        But if you delete a condition or add a new one, you’ll need to review
        parts of this form again, including statements and supporting evidence.
      </p>
    </va-alert>
  );
}

ConditionsSideNavResetAlert.propTypes = {
  formData: PropTypes.object.isRequired,
};

export default ConditionsSideNavResetAlert;
